import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import {
    CustomerAuthUser,
    CustomerLoginRequest,
    CustomerRegisterRequest,
} from '../../models/customer';
import { Customer } from '../types/customer';

export const useCustomerLogin = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CustomerAuthUser>, Error, CustomerLoginRequest>({
        mutationFn: async credentials => {
            const { data } = await httpClient.post<ApiResponse<CustomerAuthUser>>(
                API_ENDPOINTS.CUSTOMERS.LOGIN,
                credentials
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
        },
    });
};

export const useCustomerRegister = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CustomerAuthUser>, Error, CustomerRegisterRequest>({
        mutationFn: async credentials => {
            const { data } = await httpClient.post<ApiResponse<CustomerAuthUser>>(
                API_ENDPOINTS.CUSTOMERS.REGISTER,
                credentials
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
        },
    });
};

export const useCustomerLogout = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error>({
        mutationFn: async () => {
            await httpClient.post(API_ENDPOINTS.CUSTOMERS.LOGOUT);
        },
        onSuccess: () => {
            queryClient.clear();
        },
    });
};

export const useGetCustomerProfile = (options?: { enabled?: boolean }) => {
    const nullResponse = {
        data: null,
    } as ApiResponse<null>;

    return useQuery<ApiResponse<CustomerAuthUser | null>>({
        queryKey: ['customer-profile'],
        queryFn: async () => {
            const { data, status } = await httpClient.get<ApiResponse<CustomerAuthUser | null>>(
                API_ENDPOINTS.CUSTOMERS.PROFILE
            );
            if (status === 401) {
                return nullResponse;
            }
            return data;
        },
        enabled: options?.enabled !== false,
    });
};

export const useCustomerChangePassword = () => {
    return useMutation<
        ApiResponse<void>,
        Error,
        {
            current_password: string;
            password: string;
            password_confirmation: string;
        }
    >({
        mutationFn: async passwordData => {
            const { data } = await httpClient.post<ApiResponse<void>>(
                API_ENDPOINTS.CUSTOMERS.CHANGE_PASSWORD,
                passwordData
            );
            return data;
        },
    });
};

export const useUpdateCustomerProfile = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Customer>, Error, UpdateProfileRequest>({
        mutationFn: async profileData => {
            const { data } = await httpClient.put<ApiResponse<Customer>>(
                API_ENDPOINTS.TRADER.ACCOUNT.UPDATE_PROFILE,
                profileData
            );
            return data;
        },
        onSuccess: () => {
            // Invalidate and refetch profile data
            queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
        },
    });
};

export const useChangePassword = () => {
    return useMutation<ApiResponse<{ message: string }>, Error, ChangePasswordRequest>({
        mutationFn: async passwordData => {
            const { data } = await httpClient.post<ApiResponse<{ message: string }>>(
                API_ENDPOINTS.CUSTOMERS.CHANGE_PASSWORD,
                passwordData
            );
            return data;
        },
    });
};

interface UpdateProfileRequest {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    country?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
}

interface ChangePasswordRequest {
    current_password: string;
    password: string;
    password_confirmation: string;
}
